import { useState, useEffect, useRef } from "react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import { FileText, Loader2, Download } from "lucide-react";
import POSHeader from "./POSHeader";

export default function RecibosView({ authAxios, onBack }) {
  const authAxiosRef = useRef(authAxios);
  authAxiosRef.current = authAxios;
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    authAxiosRef.current.get("/pos/invoices")
      .then(r => setInvoices(r.data || []))
      .catch(() => toast.error("Erro ao carregar recibos"))
      .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDownloadPdf = async (inv) => {
    try {
      const { jsPDF } = await import("jspdf");
      const doc = new jsPDF({ unit: "mm", format: "a4" });
      doc.setFontSize(18); doc.setFont("helvetica", "bold");
      doc.text("RECIBO", 105, 20, { align: "center" });
      doc.setFontSize(10); doc.setFont("helvetica", "normal");
      doc.text(inv.invoice_number, 105, 27, { align: "center" });
      let y = 40;
      doc.setFontSize(11); doc.setFont("helvetica", "bold");
      doc.text(inv.restaurant_name || "Restaurante", 20, y);
      doc.setFont("helvetica", "normal"); doc.setFontSize(9);
      if (inv.restaurant_address) { y += 5; doc.text(inv.restaurant_address, 20, y); }
      if (inv.restaurant_phone) { y += 5; doc.text(`Tel: ${inv.restaurant_phone}`, 20, y); }
      if (inv.restaurant_nif) { y += 5; doc.text(`NIF: ${inv.restaurant_nif}`, 20, y); }
      let cy = 40;
      doc.setFont("helvetica", "bold"); doc.text("CLIENTE", 140, cy);
      doc.setFont("helvetica", "normal");
      if (inv.customer_name) { cy += 5; doc.text(inv.customer_name, 140, cy); }
      if (inv.customer_nif) { cy += 5; doc.text(`NIF/DNI: ${inv.customer_nif}`, 140, cy); }
      const dt = new Date(inv.created_at);
      cy += 5; doc.text(`Data: ${dt.toLocaleDateString("pt-PT")} ${dt.toLocaleTimeString("pt-PT", { hour: "2-digit", minute: "2-digit" })}`, 140, cy);
      y = Math.max(y, cy) + 15;
      doc.setFillColor(34, 139, 34); doc.rect(20, y - 4, 170, 8, "F");
      doc.setTextColor(255); doc.setFontSize(9); doc.setFont("helvetica", "bold");
      doc.text("Produto", 22, y); doc.text("Qtd", 120, y, { align: "center" });
      doc.text("Preco", 145, y, { align: "right" }); doc.text("Total", 185, y, { align: "right" });
      doc.setTextColor(0); doc.setFont("helvetica", "normal"); y += 8;
      (inv.items || []).forEach(item => {
        doc.text(item.product_name || "", 22, y);
        doc.text(String(item.quantity), 120, y, { align: "center" });
        doc.text(item.unit_price?.toFixed(2) || "0.00", 145, y, { align: "right" });
        doc.text(item.total?.toFixed(2) || "0.00", 185, y, { align: "right" });
        y += 6;
      });
      y += 4; doc.line(120, y, 190, y); y += 6;
      doc.text("Base:", 130, y); doc.text(`${inv.net_amount?.toFixed(2)} EUR`, 185, y, { align: "right" }); y += 5;
      doc.text(`IVA (${inv.tax_rate}%):`, 130, y); doc.text(`${inv.tax_amount?.toFixed(2)} EUR`, 185, y, { align: "right" }); y += 6;
      doc.setFont("helvetica", "bold"); doc.setFontSize(12);
      doc.text("TOTAL:", 130, y); doc.text(`${inv.total?.toFixed(2)} EUR`, 185, y, { align: "right" });
      y += 15; doc.setFontSize(8); doc.setTextColor(150);
      doc.text("Documento gerado por Kaiso System", 105, y, { align: "center" });
      doc.save(`${inv.invoice_number}.pdf`);
      toast.success("PDF descarregado!");
    } catch { toast.error("Erro ao gerar PDF"); }
  };

  const formatTime = (iso) => {
    if (!iso) return "";
    try { return new Date(iso).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" }); } catch { return ""; }
  };

  return (
    <div className="min-h-screen bg-[#f5f5f5] flex flex-col" data-testid="pos-recibos">
      <POSHeader title="Recibos do Dia" icon={FileText} onBack={onBack} />
      <ScrollArea className="flex-1">
        {loading ? (
          <div className="text-center py-12"><Loader2 className="w-6 h-6 animate-spin mx-auto text-emerald-600" /></div>
        ) : invoices.length === 0 ? (
          <div className="text-center py-12 text-gray-400">
            <FileText className="w-10 h-10 mx-auto mb-2 opacity-30" />
            <p className="text-sm">Nenhum recibo emitido hoje</p>
          </div>
        ) : (
          <div className="divide-y max-w-lg mx-auto">
            {invoices.map(inv => (
              <div key={inv.id} className="px-4 py-3 flex items-center gap-3 bg-white" data-testid={`recibo-${inv.invoice_number}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="font-bold text-xs text-emerald-700">{inv.invoice_number}</span>
                  </div>
                  <p className="text-[11px] text-gray-500">
                    {inv.customer_name || "Consumidor final"} {inv.customer_nif ? `- NIF: ${inv.customer_nif}` : ""}
                  </p>
                  <p className="text-[10px] text-gray-400">{formatTime(inv.created_at)}</p>
                </div>
                <span className="text-base font-bold text-emerald-600">{inv.total?.toFixed(2)}</span>
                <button
                  onClick={() => handleDownloadPdf(inv)}
                  className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center hover:bg-emerald-100 transition"
                  data-testid={`recibo-pdf-${inv.invoice_number}`}
                >
                  <Download className="w-4 h-4 text-emerald-600" />
                </button>
              </div>
            ))}
          </div>
        )}
      </ScrollArea>
    </div>
  );
}
